/**
 * Phase 4 — AI-assisted client replies (review-before-send). Client for the manager draft endpoints.
 *
 * Note what is NOT here: there is no "generate and send" call. Suggesting a reply and sending one are
 * separate round trips, and only approve() reaches the client. That mirrors the backend, where a
 * draft lives in its own table precisely because saving a Message sends it.
 */

export interface ReplySuggestionDto {
  templateKey: string;
  title: string;
  body: string;
  /** What a human must fill before this is safe to send. Empty = one-click sendable. */
  gaps: string[];
  reviewerNote?: string;
}

export interface SuggestionsResponse {
  suggestions: ReplySuggestionDto[];
  /** Why templates were withheld (e.g. the court's of-record flag is unset). */
  blockers: string[];
  /** The context the drafts were built from, so the panel can show and correct it. */
  source: string | null;
  stage: string;
  isCourtOfRecord: boolean | null;
}

export type ReplyDraftStatus = 'PENDING_REVIEW' | 'SENT' | 'DISCARDED';

export interface ReplyDraftDto {
  MessageDraft_guid: string;
  MessageDraft_Body: string;
  MessageDraft_OriginalBody: string;
  MessageDraft_TemplateKey: string;
  MessageDraft_Stage: string;
  MessageDraft_Source: string | null;
  MessageDraft_Gaps: string[];
  MessageDraft_Status: ReplyDraftStatus;
  MessageDraft_CreatedAt: string;
  /** True when the body still contains an unfilled blank. */
  needsAttention: boolean;
}

/** The placeholder the backend leaves where it could not fill a value. Must match draftService. */
export const DRAFT_BLANK = '[ — ]';

async function readError(response: Response, fallback: string): Promise<string> {
  try {
    const data = await response.json();
    return data?.message || fallback;
  } catch {
    return fallback;
  }
}

class ReplyDraftApi {
  private static host(): string {
    return process.env.API_HOST?.trim() || window.location.origin;
  }

  private static base(): string {
    return `${ReplyDraftApi.host()}/api/v1/manager`;
  }

  /** Candidate replies for a case. Nothing is persisted or sent. */
  static async getSuggestions(ticketGuid: string): Promise<SuggestionsResponse> {
    const response = await fetch(
      `${ReplyDraftApi.base()}/ticket/${ticketGuid}/reply-suggestions`,
      { credentials: 'include' },
    );
    if (!response.ok) {
      throw new Error(await readError(response, 'Failed to load reply suggestions'));
    }
    return response.json();
  }

  /** Commit a suggestion to the review queue. */
  static async createDraft(ticketGuid: string, templateKey: string): Promise<ReplyDraftDto> {
    const response = await fetch(`${ReplyDraftApi.base()}/ticket/${ticketGuid}/reply-drafts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ templateKey }),
    });
    if (!response.ok) {
      throw new Error(await readError(response, 'Failed to create the draft'));
    }
    return response.json();
  }

  static async listDrafts(ticketGuid: string): Promise<ReplyDraftDto[]> {
    const response = await fetch(`${ReplyDraftApi.base()}/ticket/${ticketGuid}/reply-drafts`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(await readError(response, 'Failed to load drafts'));
    }
    const data = await response.json();
    return data.drafts ?? [];
  }

  static async updateDraft(draftGuid: string, body: string): Promise<ReplyDraftDto> {
    const response = await fetch(`${ReplyDraftApi.base()}/reply-draft/${draftGuid}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ body }),
    });
    if (!response.ok) {
      throw new Error(await readError(response, 'Failed to save the draft'));
    }
    return response.json();
  }

  /**
   * Approve and send to the client. `force` is required when the body still has blanks — the server
   * refuses otherwise, so the UI must have made the user look at them first.
   */
  static async approve(draftGuid: string, force = false): Promise<ReplyDraftDto> {
    const response = await fetch(`${ReplyDraftApi.base()}/reply-draft/${draftGuid}/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ force }),
    });
    if (!response.ok) {
      throw new Error(await readError(response, 'Failed to send the draft'));
    }
    return response.json();
  }

  /**
   * Set the case's referral source. This decides pricing and which intro/offer replies apply, so it
   * is editable from the Replies panel — that is where its absence actually blocks someone.
   */
  static async setReferralSource(
    ticketGuid: string,
    referralSource: string | null,
  ): Promise<{ Ticket_ReferralSource: string | null }> {
    const response = await fetch(
      `${ReplyDraftApi.base()}/ticket/${ticketGuid}/referral-source`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ referralSource }),
      },
    );
    if (!response.ok) {
      throw new Error(await readError(response, 'Failed to set the referral source'));
    }
    return response.json();
  }

  /**
   * Set whether this is a traffic or a non-traffic matter (Lloyd, Aug 6).
   *
   * Returns what the choice actually did — `trafficAutomation: false` and a note explaining that no
   * letter of representation will be generated — so the UI can say so at the moment of the change
   * rather than leaving someone to notice later that the LOR never appeared.
   */
  static async setMatterType(
    ticketGuid: string,
    matterType: string | null,
  ): Promise<{
    Ticket_MatterType: string | null;
    trafficAutomation: boolean;
    note: string | null;
  }> {
    const response = await fetch(
      `${ReplyDraftApi.base()}/ticket/${ticketGuid}/matter-type`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ matterType }),
      },
    );
    if (!response.ok) {
      throw new Error(await readError(response, 'Failed to set the case type'));
    }
    return response.json();
  }

  static async discard(draftGuid: string): Promise<ReplyDraftDto> {
    const response = await fetch(`${ReplyDraftApi.base()}/reply-draft/${draftGuid}/discard`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(await readError(response, 'Failed to discard the draft'));
    }
    return response.json();
  }
}

export default ReplyDraftApi;
